import type {
  PackageSummaryInvoice,
  PackageSummaryOrder,
} from "react-lib/apps/MEH/types/PackageOrderSummaryType";
import { formatDateToYYYYMMDD, formatDatetime } from "react-lib/utils/dateUtils";

// ---------- ตัวกรอง ----------

export type OrderFilter = {
  search?: string;
  payment_channel?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
};

export const CHANNEL_OPTIONS = [
  { value: "", text: "ทั้งหมด" },
  { value: "COUNTER", text: "ชำระหน้าร้าน" },
  { value: "2C2P", text: "2C2P" },
  { value: "SLIP", text: "ส่งสลิปโอนเงิน" },
];

export const STATUS_OPTIONS = [
  { value: "", text: "ทั้งหมด" },
  { value: "PAID", text: "ชำระแล้ว" },
  { value: "BILLED", text: "รอชำระ" },
];

/** ตัดค่าว่าง และแปลงวันที่ DD/MM/YYYY (พ.ศ.) เป็น YYYY-MM-DD ที่ API ใช้ */
export const cleanFilter = (filter: OrderFilter): OrderFilter => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === "") continue;
    result[key] = key === "from_date" || key === "to_date" ? formatDateToYYYYMMDD(value) : value;
  }
  return result as OrderFilter;
};

// ---------- รายการสั่งซื้อ ----------

/** แถวของ BIL/invoice-with-package-order */
export type InvoiceItem = Partial<Omit<PackageSummaryInvoice, "orders">> & {
  orders?: InvoiceOrderItem[];
};

export type InvoiceOrderItem = Partial<PackageSummaryOrder>;

export type OrderItemRow = {
  id: number;
  code: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
};

export type OrderRow = {
  id: number;
  code: string;
  customer: string;
  created: string;
  channels: string[];
  status: string;
  amount: number;
  paidAt: string;
  items: OrderItemRow[];
};

const CHANNEL_NAMES: Record<string, string> = {
  COUNTER: "ชำระหน้าร้าน",
  "2C2P": "2C2P",
  SLIP: "ส่งสลิปโอนเงิน",
};

const STATUS_NAMES: Record<string, string> = {
  PAID: "ชำระแล้ว",
  BILLED: "รอชำระ",
  CANCELED: "ยกเลิก",
};

const amountOf = (value?: string | number | null): number => Number(value) || 0;

const orderItemRowOf = (order: InvoiceOrderItem): OrderItemRow => {
  const quantity = amountOf(order.quantity);
  const total = amountOf(order.price_full);

  return {
    id: order.id ?? 0,
    code: order.product_code ?? "",
    name: order.product_name ?? "",
    quantity,
    unitPrice: quantity > 0 ? total / quantity : 0,
    total,
  };
};

/** ใบแจ้งหนี้ -> แถว; ราคาต่อหน่วย = price_full / quantity ของรายการ */
export const orderRowsFromApi = (items?: InvoiceItem[] | null): OrderRow[] =>
  (items ?? [])
    .filter((invoice) => typeof invoice?.id === "number")
    .map((invoice) => ({
      id: invoice.id as number,
      code: invoice.code ?? "",
      customer: invoice.patient_name ?? "",
      created: invoice.created ? formatDatetime(invoice.created) : "",
      channels: (invoice.payment_channels ?? []).map(
        (channel) => CHANNEL_NAMES[channel] ?? channel,
      ),
      status: STATUS_NAMES[invoice.status_name ?? ""] ?? invoice.status_name ?? "",
      amount: amountOf(invoice.price),
      paidAt: invoice.paid_at ? formatDatetime(invoice.paid_at) : "",
      items: (invoice.orders ?? []).map(orderItemRowOf),
    }));

export const sumAmount = (rows: OrderRow[]): number =>
  rows.reduce((sum, row) => sum + row.amount, 0);
